'use client';

import React, { useContext, useState, useRef, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import Layout from '../Layout';
import { parsePqidatFile, uploadNewRecords, analyzeFileData } from '../../utils/processMeasurements';
import NewRecordsTable from './NewRecordsTable';
import { ArrowUpTrayIcon, CloudArrowDownIcon, CloudArrowUpIcon, DocumentMagnifyingGlassIcon, ShieldCheckIcon, TableCellsIcon, XCircleIcon, ExclamationTriangleIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';


// --- Sub-Component for Centralized Feedback ---
// By defining it here, we keep everything related to the upload flow in one file.
function FeedbackPanel({ type, message }) {
  if (type === 'idle' || !message) return null;

  const styles = {
    error: 'bg-red-50 border-red-400 text-red-800',
    warning: 'bg-orange-50 border-orange-400 text-orange-800',
    success: 'bg-green-50 border-green-400 text-green-800',
    processing: 'bg-blue-50 border-blue-400 text-blue-800',
  };

  const icons = {
  error: <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />,
  warning: <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" aria-hidden="true" />,
  success: <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />,
  processing: <ArrowPathIcon className="animate-spin h-5 w-5 text-blue-400" aria-hidden="true" />,
};

  return (
    <div className={`p-4 rounded-lg border-l-4 ${styles[type]}`}>
      <div className="flex">
        <div className="flex-shrink-0">{icons[type]}</div>
        <div className="ml-3"><p className="text-sm font-medium">{message}</p></div>
      </div>
    </div>
  );
}

export default function Upload() {
  const { user } = useContext(AuthContext);

  // --- Refactored State ---
  const [feedback, setFeedback] = useState({ type: 'idle', message: '' });
  const [newRecords, setNewRecords] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [measurementsLeft, setMeasurementsLeft] = useState(null);
  const [isQuotaLoading, setIsQuotaLoading] = useState(true);
  const [fileAnalysis, setFileAnalysis] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const fileInputRef = useRef(null);
  const [displayedCount, setDisplayedCount] = useState(5);

  // Fetch remaining measurements on page load
  useEffect(() => {
    const fetchQuota = async () => {
      if (!user) return;
      try {
        const { data: orgMember, error: orgError } = await supabase.from('organization_members').select('organization_id').eq('user_id', user.id).single();
        if (orgError || !orgMember) throw new Error('User not in an organization');
        const { data, error } = await supabase.functions.invoke('get-team-usage', { body: { organizationId: orgMember.organization_id } });
        if (error) throw new Error(`Failed to fetch team usage: ${error.message}`);
        setMeasurementsLeft(data.measurementsLeft);
      } catch (err) {
        console.error('Error fetching measurement quota:', err.message);
        setFeedback({ type: 'error', message: 'Failed to fetch measurement quota. Please try again later.' });
      } finally {
        setIsQuotaLoading(false);
      }
    };
    fetchQuota();
  }, [user]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessing(true);
    setNewRecords(null);

    try {
      if (!file.name.toLowerCase().endsWith('.pqidat')) {
        throw new Error('Invalid file type. Please upload a valid .pqidat file.');
      }

      const { records: pqidatRecords, corruptedRowCount } = await parsePqidatFile(file);
      let warnings = [];
      if (corruptedRowCount > 0) {
        warnings.push(`${corruptedRowCount} corrupted row(s) were ignored.`);
      }
      if (pqidatRecords.length === 0) {
        throw new Error('This file contains no structurally valid data rows to process.');
      }
      
      const analysis = analyzeFileData(pqidatRecords);
      if (!analysis.hasValidData) {
        throw new Error('Valid rows were found, but none have a recognizable Date/Time format.');
      }
      
      // *** THE FIX IS HERE ***
      // Correctly check for all or partial missing GPS data.
      if (analysis.recordsWithGPS === 0 && analysis.recordsWithoutGPS > 0) {
          warnings.push('This file contains no GPS data. They will be uploaded, but cannot be mapped to a project.');
      } else if (analysis.recordsWithGPS > 0 && analysis.recordsWithoutGPS > 0) {
          warnings.push(`${analysis.recordsWithoutGPS} records are missing GPS data. They will be uploaded, but cannot be mapped to a project.`);
      }

      const { data: results, error: functionError } = await supabase.functions.invoke('check-duplicates', {
          body: { newRecords: pqidatRecords },
      });
      if (functionError) throw functionError;
      
      if (results.newRecords.length === 0) {
        throw new Error('All valid measurements in this file were identified as duplicates of existing records.');
      }

      let newRecordsFound = results.newRecords;
      if (measurementsLeft !== null && newRecordsFound.length > measurementsLeft) {
        warnings.push(`Your quota is ${measurementsLeft}. Only the first ${measurementsLeft} of ${newRecordsFound.length} new records are shown.`);
        newRecordsFound = newRecordsFound.slice(0, measurementsLeft);
      }

      setNewRecords(newRecordsFound);
      setSelectedRecords(newRecordsFound);
      setFileAnalysis(analysis);
      setFeedback({ type: warnings.length > 0 ? 'warning' : 'idle', message: warnings.join(' ') });

    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };


  const handleUpload = async () => {
    if (selectedRecords.length === 0) {
      setFeedback({ type: 'warning', message: 'Please select at least one record to upload.'});
      return;
    }

    setFeedback({ type: 'processing', message: 'Uploading measurements...' });
    
    try {
      await uploadNewRecords(selectedRecords, user.id);
      resetFlow();
      setFeedback({ type: 'success', message: 'Measurements uploaded successfully!' });
      // You can add a quota refresh call here if needed
    } catch (err) {
      setFeedback({ type: 'error', message: `Upload failed: ${err.message}` });
    }
  };

  const resetFlow = () => {
    setNewRecords(null);
    setSelectedRecords([]);
    setFileAnalysis(null);
    setFeedback([]);
    // Keep feedback from successful upload
  };
  
  const displayedRecords = newRecords?.slice(0, displayedCount);

  const handleShowMore = () => setDisplayedCount(prev => prev + 10);

  const toggleRecordSelection = (record) => {
    setSelectedRecords((prevSelected) =>
      prevSelected.includes(record)
        ? prevSelected.filter((r) => r !== record)
        : [...prevSelected, record]
    );
  };

  const toggleSelectAll = () => {
    if (selectedRecords.length === newRecords.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords([...newRecords]);
    }
  };

  const steps = [
    { name: 'Upload File', description: 'Select an un-edited .pqidat file from your gauge.', icon: CloudArrowUpIcon },
    { name: 'Validate Data', description: 'We check for corrupted rows and valid data formats.', icon: ShieldCheckIcon },
    { name: 'Prevent Duplicates', description: 'New data is checked against existing records.', icon: DocumentMagnifyingGlassIcon },
    { name: 'Confirm & Import', description: 'Review the list of new measurements and approve.', icon: TableCellsIcon },
  ];

  


  return (
    <Layout noPadding={true}>
      <div className="mx-auto p-2">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {/* --- HEADER --- */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg flex justify-between items-center">
            
            
            <div>
              <h2 className="text-base font-semibold text-gray-900">Upload Measurements</h2>
              <p className="text-sm text-gray-600">Import new data from a TransTech PQI gauge.</p>
            </div>


            <div className="flex items-center space-x-2">
              {isQuotaLoading ? (
                <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 inset-ring inset-ring-blue-700/10 dark:bg-blue-400/10 dark:text-blue-400 dark:inset-ring-blue-400/30">Loading quota...</span>
              ) : measurementsLeft !== null ? (
                <span
                  className={`inline-flex items-center rounded-md bg-green-50 px-3 py-2 text-xs font-medium border ${
                    measurementsLeft > 0
                      ? 'border-green-600/20 text-green-700 inset-ring inset-ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:inset-ring-green-500/20'
                      : 'border-red-600/10 text-red-700 inset-ring inset-ring-red-600/10 dark:bg-red-400/10 dark:text-red-400 dark:inset-ring-red-400/20'
                  }`}
                >
                  {measurementsLeft} Measurements Left This Cycle
                </span>
              ) : (
                <span className="text-sm text-red-500">Quota unavailable</span>
              )}
            </div>
          </div>

          {/* --- CONTENT AREA --- */}
          <div className="p-6">

            {/* ===== MODIFICATION START ===== */}
            {/* The step cards are now outside the conditional logic, so they always show. */}
            <div className="space-y-6">
              <div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  {steps.map((step) => (
                    <div key={step.name} className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex items-start gap-4">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-white border shadow-sm">
                        <step.icon className="h-6 w-6 text-amber-700" aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="text-base font-semibold text-gray-900">{step.name}</h4>
                        <p className="text-sm text-gray-500">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            <FeedbackPanel type={feedback.type} message={feedback.message} />


              {/* This is now the main content switcher. It shows the table OR the upload/processing box. */}
              {newRecords ? (
                // --- CONFIRMATION STATE (Replaces the dropzone) ---
                <div className="flex flex-col space-y-6">
                  <NewRecordsTable
                    records={newRecords}
                    displayedCount={displayedCount}
                    onShowMore={handleShowMore}
                    selectedRecords={selectedRecords}
                    onRecordSelect={toggleRecordSelection}
                    onSelectAll={toggleSelectAll}
                  />
                  <div className="flex justify-end items-center space-x-3 pt-4 border-t border-gray-200 mt-4">
  <button 
    onClick={resetFlow} 
    className="px-5 py-2 bg-white text-gray-800 text-sm font-semibold rounded-lg border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
  >
    Cancel
  </button>
  <button 
    onClick={handleUpload} 
    className="group inline-flex items-center justify-center rounded-lg bg-yellow-600 px-5 py-2 text-sm font-semibold text-white shadow-md transition-all hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-60 disabled:pointer-events-none"
    disabled={selectedRecords.length === 0}
  >
    
    <span>
      Upload {selectedRecords.length} Record{selectedRecords.length !== 1 ? 's' : ''}
    </span>
    <ArrowUpTrayIcon 
      className="h-5 w-5 ml-2 transform transition-transform duration-200 ease-in-out group-hover:-translate-y-1" 
      aria-hidden="true" 
    />
  </button>
</div>
                </div>
              ) : (
                // --- WELCOME / EMPTY / PROCESSING STATE ---
                // The container is always here. Its content changes based on `isProcessing`.
                <div
                  onClick={() => !isProcessing && fileInputRef.current && fileInputRef.current.click()}
                  className={`relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center transition-colors ${!isProcessing ? 'hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer' : ''}`}
                >
                  {isProcessing ? (
                     <div className="flex flex-col items-center">
                        <svg className="animate-spin mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="mt-2 block text-sm font-semibold text-gray-900">Processing File...</span>
                        <span className="mt-1 block text-xs text-gray-500">Validating data and checking for duplicates</span>
                     </div>
                  ) : isQuotaLoading ? (
                    <div className="flex flex-col items-center">
                      <CloudArrowDownIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <span className="mt-2 block text-sm font-semibold text-gray-900">Loading Your Quota</span>
                      <span className="mt-1 block text-xs text-gray-500">Checking to see how many measurements are left in this cycle</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <span className="mt-2 block text-sm font-semibold text-gray-900">Click to upload</span>
                      <span className="mt-1 block text-xs text-gray-500">.pqidat files only</span>
                    </div>
                  )}
                </div>
              )}
            </div>
             {/* ===== MODIFICATION END ===== */}

          </div>
        </div>
      </div>
      <input type="file" disabled={isProcessing || isQuotaLoading || measurementsLeft < 1} ref={fileInputRef} onChange={handleFileChange} accept=".pqidat" className="hidden"/>
    </Layout>
  );
}